import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from "react-router-dom";
import Nav from "../../components/Nav/Nav";
import { ContextTicketDto, TicketStatusNameType } from "../HomeView/HomeView";
import "./TicketsView.scss";
import { getStatusColor } from "../../utils/GetStatusColor";

interface SearchFilters {
  Ticket_Number: string;
  Ticket_Citation: string;
  Ticket_City: string;
}

const initialFilters: SearchFilters = {
  Ticket_Number: '',
  Ticket_Citation: '',
  Ticket_City: ''
};

const RESULTS_PER_PAGE = 20;

const TicketsView: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<ContextTicketDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  const fetchTickets = useCallback(async (
    page = 0,
    searchFilters: SearchFilters = initialFilters,
    appendResults = false,
    markAsSearched = false,
  ) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('from', (page * RESULTS_PER_PAGE).toString());
      params.append('size', RESULTS_PER_PAGE.toString());

      if (searchFilters.Ticket_Number.trim()) {
        params.append('Ticket_Number', searchFilters.Ticket_Number.trim());
      }
      if (searchFilters.Ticket_Citation.trim()) {
        params.append('Ticket_Citation', searchFilters.Ticket_Citation.trim());
      }
      if (searchFilters.Ticket_City.trim()) {
        params.append('Ticket_City', searchFilters.Ticket_City.trim());
      }

      const url = `${process.env.API_HOST || ''}/api/v1/tickets?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tickets: ${response.status} ${response.statusText}`);
      }

      const ticketsData: ContextTicketDto[] = await response.json();

      if (appendResults) {
        setTickets((prevTickets) => [...prevTickets, ...ticketsData]);
      } else {
        setTickets(ticketsData);
      }

      setHasMoreData(ticketsData.length === RESULTS_PER_PAGE);
      setCurrentPage(page);
      if (markAsSearched) {
        setHasSearched(true);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets(0, initialFilters, false, false);
  }, [fetchTickets]);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setHasMoreData(false);
    fetchTickets(0, filters, false, true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(0);
    setHasMoreData(false);
    setHasSearched(false);
    fetchTickets(0, initialFilters, false, false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    fetchTickets(nextPage, filters, true, hasSearched);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTicketClick = (ticketGuid: string) => {
    navigate(`/ticket?Ticket_guid=${ticketGuid}`);
  };

  const formatCourtDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return '—';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const showEmptySearchState = !loading && tickets.length === 0 && hasSearched;
  const showEmptyInitialState = !loading && tickets.length === 0 && !hasSearched;

  return (
    <>
      <Nav />
      <Container maxWidth="lg" className="tickets-view">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" className="tickets-view-title">
            My Tickets
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/new-ticket')}
            size="large"
          >
            New Ticket
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search Filters */}
        <Paper className="search-filters" elevation={1}>
          <Typography variant="h6" gutterBottom>
            Search Tickets
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Ticket Number"
              value={filters.Ticket_Number}
              onChange={(e) => handleFilterChange('Ticket_Number', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Citation"
              value={filters.Ticket_Citation}
              onChange={(e) => handleFilterChange('Ticket_Citation', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="City"
              value={filters.Ticket_City}
              onChange={(e) => handleFilterChange('Ticket_City', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading || loadingMore}
            >
              Search Tickets
            </Button>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={loading || loadingMore}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>Loading tickets...</Typography>
          </Box>
        )}

        {/* Empty State */}
        {showEmptySearchState && (
          <Paper className="tickets-view-empty">
            <Box textAlign="center" py={6}>
              <ReceiptIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No tickets found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search criteria.
              </Typography>
            </Box>
          </Paper>
        )}

        {showEmptyInitialState && (
          <Paper className="tickets-view-empty">
            <Box textAlign="center" py={6}>
              <ReceiptIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No tickets submitted yet
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You haven&apos;t submitted any tickets yet.
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Tickets Table */}
        {!loading && tickets.length > 0 && (
          <TableContainer component={Paper} className="tickets-table">
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
                {hasMoreData ? ' (more available)' : ''}
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket Number</TableCell>
                  <TableCell>Citation</TableCell>
                  <TableCell>Representation</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Court Date</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((contextTicket) => (
                  <TableRow
                    key={contextTicket.ticket.Ticket_guid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleTicketClick(contextTicket.ticket.Ticket_guid)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ fontWeight: 'medium' }}
                      >
                        {contextTicket.ticket.Ticket_Number}
                      </Typography>
                    </TableCell>
                    <TableCell>{contextTicket.ticket.Ticket_Citation}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {contextTicket.ticket.Ticket_Representation || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {contextTicket.ticketStatus ? (
                        <Chip
                          label={contextTicket.ticketStatus.TicketStatus_Name}
                          color={getStatusColor(contextTicket.ticketStatus.TicketStatus_Name as TicketStatusNameType)}
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="active"
                          color="primary"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {formatCourtDate(contextTicket.ticket.Ticket_CourtDate)}
                    </TableCell>
                    <TableCell>
                      {new Date(contextTicket.ticket.Ticket_CreatedAt).toLocaleDateString('en-US')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasMoreData && (
              <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                >
                  {loadingMore ? 'Loading...' : `Load More (Showing ${tickets.length} results)`}
                </Button>
              </Box>
            )}
          </TableContainer>
        )}
      </Container>
    </>
  );
};

export default TicketsView;
